<script setup>
import TabBarItem from "@/components/Tabbar/TabBarItem.vue";
import {computed, ref, watch} from "vue";

// Определение пропсов
const props = defineProps({
  // Массив вкладок (строки или объекты)
  tabs: {
    type: Array,
    required: true,
    default: () => [],
    validator: (tabs) => Array.isArray(tabs) && tabs.length > 0
  },
  // Изначально активная вкладка (по индексу или значению)
  defaultActiveTab: {
    type: [String, Number, Object],
    default: null
  },
  // Внешний контроль активной вкладки (v-model)
  modelValue: {
    type: [String, Number, Object],
    default: null
  },
  // Ключ для извлечения уникального идентификатора из объекта
  keyField: {
    type: String,
    default: "id"
  },
  // Ключ для извлечения отображаемого текста из объекта
  labelField: {
    type: String,
    default: "label"
  },
  // Ключ для извлечения состояния disabled из объекта
  disabledField: {
    type: String,
    default: "disabled"
  },
  // Растягивать элементы на всю ширину
  fullWidth: {
    type: Boolean,
    default: false
  }
});

// Определение событий
const emit = defineEmits(["update:modelValue", "tab-change"]);

// Реактивная ссылка на активную вкладку
const activeItem = ref(null);

// Вспомогательные функции для работы с разными типами данных
const getTabKey = (tab) => {
  if (typeof tab === "string" || typeof tab === "number") {
    return tab;
  }
  return tab[props.keyField] || tab.id || tab;
};

const getTabLabel = (tab) => {
  if (typeof tab === "string" || typeof tab === "number") {
    return String(tab);
  }
  return tab[props.labelField] || tab.label || tab.name || String(tab);
};

const getTabDisabled = (tab) => {
  if (typeof tab === "string" || typeof tab === "number") {
    return false;
  }
  return Boolean(tab[props.disabledField] || tab.disabled);
};

// Функция для поиска вкладки по ключу
const findTabByKey = (key) => {
  return props.tabs.find(tab => {
    const tabKey = getTabKey(tab);
    return tabKey === key;
  });
};

// Функция для сравнения вкладок
const isTabEqual = (tab1, tab2) => {
  if (tab1 === tab2) return true;
  return getTabKey(tab1) === getTabKey(tab2);
};

// Вычисляемое свойство для определения активной вкладки
const currentActiveTab = computed({
  get: () => {
    // Приоритет: modelValue -> activeItem -> первая доступная вкладка
    const candidate = props.modelValue || activeItem.value;

    if (candidate) {
      // Если передан ключ, находим соответствующую вкладку
      if (typeof candidate === "string" || typeof candidate === "number") {
        const foundTab = findTabByKey(candidate);
        if (foundTab) return foundTab;
      }

      // Если передана сама вкладка, находим актуальную ссылку в props.tabs
      const matchingTab = props.tabs.find(tab => isTabEqual(tab, candidate));
      if (matchingTab) {
        return matchingTab;
      }
    }

    // Находим первую доступную (не отключенную) вкладку
    const firstAvailableTab = props.tabs.find(tab => !getTabDisabled(tab));
    return firstAvailableTab || props.tabs[0] || null;
  },
  set: (value) => {
    activeItem.value = value;
    emit("update:modelValue", value);
    emit("tab-change", {
      tab: value,
      key: getTabKey(value),
      label: getTabLabel(value)
    });
  }
});

// Инициализация активной вкладки
const initializeActiveTab = () => {
  if (!props.tabs.length) return;

  if (props.modelValue) {
    // Используем внешнее значение v-model
    activeItem.value = props.modelValue;
  } else if (props.defaultActiveTab !== null) {
    // Используем значение по умолчанию
    activeItem.value = typeof props.defaultActiveTab === "number"
        ? props.tabs[props.defaultActiveTab]
        : props.defaultActiveTab;
  } else {
    // Используем первую вкладку
    activeItem.value = props.tabs[0];
  }
};

// Обработчик клика по вкладке
const handleTabClick = (tab) => {
  if (getTabDisabled(tab) || isTabEqual(tab, currentActiveTab.value)) {
    return;
  }
  currentActiveTab.value = tab;
};

// Следим за изменениями modelValue
watch(() => props.modelValue, (newValue) => {
  if (newValue !== undefined && newValue !== activeItem.value) {
    activeItem.value = newValue;
  }
});

// Следим за изменениями массива вкладок И их свойств (включая disabled)
watch(() => props.tabs, (newTabs) => {
  if (!newTabs.length) {
    activeItem.value = null;
    return;
  }

  // Если активная вкладка стала disabled, переключаемся на первую доступную
  const currentTab = currentActiveTab.value;
  if (currentTab) {
    const matchingTab = newTabs.find(tab => isTabEqual(tab, currentTab));

    if (matchingTab && getTabDisabled(matchingTab)) {
      const firstAvailableTab = newTabs.find(tab => !getTabDisabled(tab));
      if (firstAvailableTab) {
        currentActiveTab.value = firstAvailableTab;
      }
      return;
    }
  }

  // Если текущая активная вкладка больше не существует, выбираем первую доступную
  if (!newTabs.some(tab => isTabEqual(tab, currentActiveTab.value))) {
    const firstAvailableTab = newTabs.find(tab => !getTabDisabled(tab));
    currentActiveTab.value = firstAvailableTab || newTabs[0];
  }
}, {
  immediate: true,
  deep: true
});

// Инициализация при монтировании
initializeActiveTab();
</script>

<template>
  <div class="tab-bar">
    <TabBarItem
      v-for="(tab) in tabs"
      :key="getTabKey(tab)"
      :is-active="isTabEqual(tab, currentActiveTab)"
      :label="getTabLabel(tab)"
      :disabled="getTabDisabled(tab)"
      :full-width="fullWidth"
      class="tab-bar__item"
      @click="handleTabClick(tab)"
    />
  </div>
</template>

<style scoped lang="scss">
.tab-bar {
  display: flex;
  gap: 10px;
  padding: 4px 8px;
  border-radius: 8px;
  flex-wrap: wrap;
  background-color: var(--background-bg-white);
}
</style>
